import { Box, useTheme } from '@mui/material';
import cx from 'classnames';
import { FC, PropsWithChildren, RefObject } from 'react';
import { createUseStyles } from 'react-jss';
import { SIDEBAR_FULL_WIDTH, SIDEBAR_WIDTH } from '../../constants';
import { UI_TEMPLATE_DRIVEN_SIDEBAR_FLAG } from '../../feature-flags';
import { FocusWithin } from '../../util/focus-within';
import { useBoolFlag } from '../flags';

const useStyles = createUseStyles<string, { isUiTemplateDrivenSidebar: boolean }>({
  footer: {
    display: 'flex',
    bottom: 0,
    boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.69)',
    zIndex: 10,
  },
  header: {
    display: 'flex',
    boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.69)',
    top: 0,
    zIndex: 10,
  },
  root: ({ isUiTemplateDrivenSidebar }) => ({
    '-ms-overflow-style': 'none',
    'scrollbar-width': 'none',
    '& ::-webkit-scrollbar': {
      display: 'none',
    },
    ...(!isUiTemplateDrivenSidebar ? {
      '& .MenuItem-root .text': {
        maxWidth: 275 - 72,
        overflow: 'hidden',
      },
    } : {}),
    ...(!isUiTemplateDrivenSidebar ? {
      '& .MuiBox-root .avatar': {
        maxWidth: 275 - 72,
        overflow: 'hidden',
      },
    } : {}),
    bottom: 0,
    boxShadow: '0px 0px 20px 0px rgba(0,0,0,0.35)',
    display: 'flex',
    flexDirection: 'column',
    left: 0,
    overflowX: 'hidden',
    overflowY: 'visible',
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    transition: [['opacity', '250ms'], ['transform', '250ms'], ['width', '250ms', 'ease']],
  }),
  static: {
    height: '100%',
    '& >.MuiBox-root': {
      height: '100%',
    },
    '& .sidebar': {
      height: '100%',
      position: 'relative',
      boxShadow: '0px 0px 2px 0px rgba(0,0,0,0.35)',
    },
  },
});

interface Props extends PropsWithChildren {
  className?: string;
  drawerOpen: boolean;
  drawerRef?: RefObject<HTMLDivElement | undefined>;
  drawerWidthCollapsed?: number;
  drawerWidthExpanded?: number;
  onDrawerClose?: () => void;
  onDrawerOpen?: () => void;
  pinSidebar: boolean;
}

export const Sidebar: FC<Props> = ({
  children,
  className,
  drawerOpen,
  drawerRef,
  drawerWidthCollapsed = SIDEBAR_WIDTH,
  drawerWidthExpanded = SIDEBAR_FULL_WIDTH,
  onDrawerClose,
  onDrawerOpen,
  pinSidebar,
}) => {
  const isUiTemplateDrivenSidebar = useBoolFlag(UI_TEMPLATE_DRIVEN_SIDEBAR_FLAG) ?? false;
  const classes = useStyles({ isUiTemplateDrivenSidebar });
  const rootClassName = cx('sidebar', className, classes.root);
  const sidebarClassName = cx('sidebar', className, classes.static);
  const theme = useTheme();
  const sidebarWidth = pinSidebar ? drawerWidthExpanded : drawerOpen ? drawerWidthExpanded : drawerWidthCollapsed;

  return (
    <FocusWithin
      onBlur={onDrawerClose}
      onFocus={onDrawerOpen}
    >
      <Box id='domain-sidebar-container' className={pinSidebar ? sidebarClassName : undefined}>
        <Box id='domain-sidebar-content'>
          <Box
            data-testid="sidebar"
            data-sidebar-open={String(pinSidebar || drawerOpen)}
            ref={drawerRef}
            className={rootClassName}
            display="flex"
            onMouseEnter={onDrawerOpen}
            onMouseLeave={onDrawerClose}
            sx={{
              backgroundColor: theme.sidebar.background,
              color: theme.sidebar.contrastText,
              width: sidebarWidth,
              zIndex: theme.zIndex.drawer + 101,
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </FocusWithin>
  );
};
